import type { JourneyPage } from './types'
import { trialCopy } from '@/utils/trialCopy'

const TRIAL_NOTE = `${trialCopy.shortLabel}. No 47-page form. No paraplanner recruitment stress.`

export const adviserOs: JourneyPage = {
  slug: 'adviser-os',
  intro: [
    { text: 'The ', highlight: 'Adviser Operating System' },
    { text: 'One platform. ', highlight: 'Your entire practice' },
    { text: 'Reports. Meetings. Compliance. ', highlight: 'Clients' },
    { text: 'Everything ', highlight: 'connects' },
    { text: 'Built by advisers. ', highlight: 'For advisers' },
    { text: 'Welcome to ', highlight: 'WealthSpace' },
  ],
  colorClass: 'adviser-os',
  seo: {
    title: 'The Adviser Operating System | WealthSpace',
    description:
      'WealthSpace is the all-in-one operating system for UK financial advisers. One platform for reports, meetings, compliance, clients and practice growth.',
    keywords:
      'adviser operating system, practice management, all-in-one platform, financial adviser software, IFA practice management, adviser technology, compliance platform',
  },
  hero: {
    eyebrow: 'The Adviser Operating System',
    title: 'One platform.',
    titleAccent: 'Your entire practice.',
    subtitle:
      'Stop juggling 15 different tools. WealthSpace is the operating system that runs your advice firm - reports, meetings, compliance, clients, and growth - all in one place.',
    microcopy: TRIAL_NOTE,
  },
  journeySection: {
    label: 'System Architecture',
    title: 'Everything',
    titleAccent: 'connects',
    subtitle:
      'Unlike bolted-together tools, WealthSpace is built as a unified system. Data flows seamlessly between modules, so you never duplicate work.',
  },
  timeline: [
    {
      time: 'Core Module',
      icon: 'sparkle',
      iconColor: 'teal',
      title: 'Sparks AI',
      meta: 'AI-powered report writing',
      body: 'Tell the AI what you discussed. It drafts suitability reports and cites its sources. You review and approve.',
      previewLabel: 'Features',
      previewText:
        'Suitability reports. File notes. Client letters. Auto-citations.',
    },
    {
      time: 'Core Module',
      icon: 'mic',
      iconColor: 'violet',
      title: 'Meeting Capture',
      meta: 'Recording & transcription',
      body: 'Record client meetings with one click. Get AI transcriptions, structured notes, and action items automatically. Never miss a detail again.',
      previewLabel: 'Features',
      previewText:
        'Recording. Transcription. AI summaries. Action items. Follow-ups.',
    },
    {
      time: 'Core Module',
      icon: 'shield',
      iconColor: 'amber',
      title: 'Compliance Engine',
      meta: 'FCA COBS checking',
      body: 'FCA rules checked automatically as you work. COBS and PROD requirements flagged before submission. Your compliance officer will be suspiciously quiet.',
      previewLabel: 'Features',
      previewText:
        'FCA COBS checking. PROD rules. Approval workflows. Audit trails. Risk flags.',
    },
    {
      time: 'Core Module',
      icon: 'users',
      iconColor: 'emerald',
      title: 'Client Hub',
      meta: 'CRM & portal',
      body: 'Everything about your clients in one place. Plus a client portal (coming soon) where they can securely access their documents and progress.',
      previewLabel: 'Features',
      previewText:
        'Client CRM. Secure portal (coming soon). Document vault.',
    },
    {
      time: 'Core Module',
      icon: 'chart',
      iconColor: 'rose',
      title: 'Planning Tools',
      meta: 'Cash flow & calculators',
      body: 'Cash flow modelling, tax calculators, and value-of-advice demonstrations. Help clients visualise their financial future and understand the impact of your advice.',
      previewLabel: 'Features',
      previewText:
        'Cash flow modelling. Tax calculators. Value of advice. Scenario planning. Projections.',
    },
  ],
  result: {
    title: 'One platform.',
    titleAccent: 'Everything you need.',
    body: 'Stop juggling 15 different tools. WealthSpace is the operating system that runs your advice firm - reports, meetings, compliance, clients, and growth - all in one place.',
    stats: [
      { value: '3hrs', label: 'Saved per report on average' },
      { value: 'One', label: 'Place for reports, meetings and clients' },
      { value: 'COBS', label: 'Compliance checks built into the workflow' },
    ],
  },
  features: {
    label: 'Integrations',
    title: 'Works with your',
    titleAccent: 'stack',
    subtitle:
      'Connect WealthSpace to the tools you already use, or let it replace them entirely.',
    items: [
      {
        icon: 'document',
        title: 'Microsoft 365',
        body: 'Full integration with Outlook, Word, and Excel. Export reports, sync calendars, and manage documents seamlessly.',
      },
      {
        icon: 'chart',
        title: 'Platform Integrations',
        body: 'Connect to Intelliflo, Transact, FE Analytics, Xplan and 12+ more platforms via our API.',
      },
      {
        icon: 'shield',
        title: 'Module by Module',
        body: 'Already invested in other systems? No problem. WealthSpace integrates via API so you can adopt it module by module.',
      },
    ],
  },
  testimonial: {
    quote:
      "We've taken on more clients without hiring another paraplanner. The suitability reports are thorough and need minimal editing.",
    authorName: 'James C.',
    authorRole: 'Managing Partner, Harbour Financial',
    initials: 'JC',
  },
  cta: {
    title: 'Ready to upgrade your',
    titleAccent: 'practice?',
    subtitle:
      "Join the growing community of UK advice firms who've made WealthSpace their operating system. Book a 30-minute demo and see the difference.",
    note: TRIAL_NOTE,
  },
}
